import type {
    DOMConversionMap,
    DOMConversionOutput,
    DOMExportOutput,
    EditorConfig,
    LexicalNode,
    NodeKey,
    SerializedTextNode,
    Spread,
} from 'lexical';

import { TextNode } from 'lexical';

export type SerializedInsertMentionNode = Spread<
    {
        id: number;
        name: string;
        type: 'insert-mention';
        version: 1;
    },
    SerializedTextNode
>

const mentionStyle = 'background-color: rgba(24, 119, 232, 0.2)';

export class InsertMentionNode extends TextNode {
    __id: number;
    __name: string;

    static getType(): string {
        return 'insert-mention'
    }

    static clone(node: InsertMentionNode): InsertMentionNode {
        return new InsertMentionNode(node.__id, node.__name, node.__text, node.__key);
    }
    constructor(id: number, name: string, text?: string, key?: NodeKey) {
        super(text?? name, key);
        this.__id = id;
        this.__name = name;
    }

    // JSON→Nodeに変換
    static importJSON(serializedNode: SerializedInsertMentionNode): InsertMentionNode {
        const node = $createInsertMentionNode(serializedNode.id, serializedNode.name);
        node.setTextContent(serializedNode.text);
        //node.setFormat(serializedNode.format);
        node.setDetail(serializedNode.detail);
        node.setMode(serializedNode.mode);
        node.setStyle(serializedNode.style);
        return node;
    }

    // Node→JSONに変換
    exportJSON(): SerializedInsertMentionNode {
        return {
            ...super.exportJSON(),
            id: this.__id,
            name: this.__name,
            type: 'insert-mention',
            version: 1,
        }
    }

    // Node→DOMに変換（内部表示用）
    createDOM(config: EditorConfig): HTMLElement {
        const dom = super.createDOM(config);
        dom.style.cssText = mentionStyle;
        dom.className = 'insert-mention';
        return dom;
    }

    // DOM→Nodeに変換
    static importDOM(): DOMConversionMap | null {
        return {
            span: (domNode: HTMLElement) => {
                if (!domNode.hasAttribute('data-insert-mention')) {
                    return null;
                }
                return {
                    conversion: (
                        domNode: HTMLElement,
                    ): DOMConversionOutput | null => {
                        const name = domNode.textContent;
                        const id = domNode.getAttribute('data-mention-id');
                        if (!name || !id) return null;

                        const node = $createInsertMentionNode(Number(id), name);
                        return { node };
                    },
                    priority: 1,
                };
            },
        };
    }

    // Node→DOMに変換（外部出力用）
    exportDOM(): DOMExportOutput {
        const element = document.createElement('span');
        element.textContent = this.__name;
        element.setAttribute('data-insert-mention', 'true');
        element.setAttribute('data-mention-id', this.__id.toString());
        return { element };
    }

    isTextEntity(): true {
        return true;
    }

    canInsertTextBefore(): boolean {
        return false;
    }

    canInsertTextAfter(): boolean {
        return false;
    }

    // フォーマットの変更禁止
    setFormat() {
        return this;
    }

    // フォーマットの変更禁止
    toggleFormat() {
        return this;
    }
}

export const $createInsertMentionNode = (id: number, name: string): InsertMentionNode => {
    const insertMentionNode = new InsertMentionNode(id, name);
    insertMentionNode.setMode('token');
    return insertMentionNode;
}

export function $isInsertMentionNode(
    node: LexicalNode | null | undefined,
): node is InsertMentionNode {
  return node instanceof InsertMentionNode;
}
